"use client";

import { useEffect, useRef, type CSSProperties } from "react";
import styles from "./home.module.css";

// The homepage's 3D ring of feature cards, ported from design/sevra-cards.html.
// Drag or fling it, step with the arrow keys, or let it turn on its own every 7s.
// The caption and glow follow the front card; once a card settles in front its
// panel plays its reveal. Everything per-frame writes to the DOM directly, so
// React renders this once and never again.

const CAPTIONS = [
  "Before a guest arrives, Sevra reads what's public about them online and writes your team a brief.",
  "Ask in plain words. Sevra blocks tables, moves parties and finds openings while you keep working.",
  "Keep a list of guests you won't seat again. When they try to book, Sevra stops them.",
  "Hold a card at booking and charge a no-show fee only if you need to. For tasting menus and events, take full prepayment instead.",
  "Message guests by text right from the reservation, and see their replies the moment they arrive.",
];
const GLOWS = [
  "rgba(232,171,95,0.24)",
  "rgba(134,174,240,0.24)",
  "rgba(238,107,88,0.22)",
  "rgba(140,199,156,0.22)",
  "rgba(184,156,240,0.24)",
];
const REPLY = "Done. Patio blocked until close. Chen, party of 4, moved to Table 12 at 7:30.";
const ASK_INDEX = 1;

const tint = (color: string) => ({ "--tint": color }) as CSSProperties;
const cx = (...names: string[]) => names.join(" ");

function Back() {
  return (
    <div className={cx(styles.face, styles.back)} aria-hidden="true">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className={styles.big} src="/brand/sevra-icon.svg" alt="" />
    </div>
  );
}

export default function FeatureRing() {
  const stageRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const typedRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const stage = stageRef.current!;
    const ring = ringRef.current!;
    const caption = captionRef.current!;
    const typed = typedRef.current!;
    const cards = Array.from(ring.children) as HTMLElement[];
    const faces = cards.map((c) => Array.from(c.children) as HTMLElement[]);
    const fxCache: string[] = cards.map(() => "");
    const N = cards.length;
    const STEP = 360 / N;

    let reduce = false;
    try {
      reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {}

    let angle = 0, target = 0, radius = 0;
    let dragging = false, startX = 0, startTarget = 0, lastX = 0, vel = 0, lastMove = 0;
    let lastInteract = performance.now();
    let front = -1, settledFront = -1;
    let typeTimer: ReturnType<typeof setInterval> | undefined;
    let typeDelay: ReturnType<typeof setTimeout> | undefined;
    let tiltX = 0, tiltY = 0, mx = 0, my = 0;
    let raf = 0;

    // Called on load and on every resize. On small screens the ring tightens to
    // match the narrower cards, so the neighbours' edges stay in view. Same query as
    // the media query in home.module.css, so card size and radius switch together.
    const small = window.matchMedia("(max-width: 520px)");
    function layout() {
      const w = ring.offsetWidth;
      const factor = small.matches ? 0.74 : N === 3 ? 0.86 : N === 4 ? 0.95 : 1.12;
      radius = Math.round(w * factor);
      cards.forEach((c, i) => {
        c.style.transform = `rotateY(${i * STEP}deg) translateZ(${radius}px)`;
      });
    }

    const norm = (a: number) => (((a % 360) + 540) % 360) - 180;
    const indexFor = (t: number) => ((Math.round(-t / STEP) % N) + N) % N;

    function goTo(i: number) {
      const cur = indexFor(target);
      let diff = (((i - cur) % N) + N) % N;
      if (diff > N / 2) diff -= N;
      target -= diff * STEP;
      lastInteract = performance.now();
    }

    function stopType() {
      clearTimeout(typeDelay);
      clearInterval(typeTimer);
    }

    function startType() {
      stopType();
      typed.textContent = "";
      if (reduce) {
        typed.textContent = REPLY;
        return;
      }
      let k = 0;
      typeDelay = setTimeout(() => {
        typeTimer = setInterval(() => {
          k++;
          typed.textContent = REPLY.slice(0, k);
          if (k >= REPLY.length) clearInterval(typeTimer);
        }, 26);
      }, 900);
    }

    function setFront(i: number) {
      caption.textContent = CAPTIONS[i];
      stage.style.setProperty("--glow", GLOWS[i]);
      cards.forEach((c, j) => c.setAttribute("aria-hidden", j === i ? "false" : "true"));
    }

    function settle(i: number) {
      cards.forEach((c, j) => c.classList.toggle(styles.live, j === i));
      if (i === ASK_INDEX) startType();
      else {
        stopType();
        typed.textContent = "";
      }
    }

    function onPointerDown(e: PointerEvent) {
      dragging = true;
      stage.classList.add(styles.dragging);
      startX = lastX = e.clientX;
      startTarget = target;
      vel = 0;
      lastMove = performance.now();
      try {
        stage.setPointerCapture(e.pointerId);
      } catch {}
      lastInteract = performance.now();
    }

    function onPointerMove(e: PointerEvent) {
      const r = stage.getBoundingClientRect();
      mx = (e.clientX - r.left) / r.width - 0.5;
      my = (e.clientY - r.top) / r.height - 0.5;
      if (!dragging) return;
      const now = performance.now();
      vel = (e.clientX - lastX) / Math.max(1, now - lastMove);
      lastX = e.clientX;
      lastMove = now;
      target = startTarget + (e.clientX - startX) * 0.32;
      lastInteract = now;
    }

    function release() {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove(styles.dragging);
      const fling = vel * 90;
      target = Math.round((target + fling) / STEP) * STEP;
      lastInteract = performance.now();
    }

    function onPointerLeave() {
      mx = 0;
      my = 0;
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goTo((indexFor(target) + 1) % N);
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goTo((indexFor(target) + N - 1) % N);
      }
    }

    let last = performance.now();
    function frame(now: number) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      if (!reduce && !dragging && now - lastInteract > 7000 && document.visibilityState === "visible") {
        target -= STEP;
        lastInteract = now;
      }

      const k = reduce ? 1 : 1 - Math.pow(0.0008, dt);
      angle += (target - angle) * (dragging ? Math.min(1, k * 2.2) : k);
      tiltX += ((reduce ? 0 : -my * 6) - tiltX) * Math.min(1, dt * 4);
      tiltY += ((reduce ? 0 : mx * 8) - tiltY) * Math.min(1, dt * 4);
      const bob = reduce ? 0 : Math.sin(now / 1800) * 1.2;

      ring.style.transform = `translateZ(${-radius}px) rotateX(${(tiltX + bob - 4).toFixed(2)}deg) rotateY(${(angle + tiltY).toFixed(2)}deg)`;

      // Cards dim and blur as they turn away from the front.
      cards.forEach((_, i) => {
        const rel = norm(i * STEP + angle);
        const f = (Math.cos((rel * Math.PI) / 180) + 1) / 2;
        const fx = `brightness(${(0.3 + 0.7 * f).toFixed(3)})` + (reduce ? "" : ` blur(${((1 - f) * 7).toFixed(2)}px)`);
        if (fxCache[i] !== fx) {
          fxCache[i] = fx;
          for (const face of faces[i]) face.style.filter = fx;
        }
      });

      const fi = indexFor(dragging ? angle : target);
      if (fi !== front) {
        front = fi;
        setFront(fi);
      }
      const settled = !dragging && Math.abs(target - angle) < 1.5;
      const s = settled ? fi : -1;
      if (s !== settledFront) {
        settledFront = s;
        if (s >= 0) settle(s);
        else cards.forEach((c) => c.classList.remove(styles.live));
      }

      raf = requestAnimationFrame(frame);
    }

    window.addEventListener("resize", layout);
    stage.addEventListener("pointerdown", onPointerDown);
    stage.addEventListener("pointermove", onPointerMove);
    stage.addEventListener("pointerup", release);
    stage.addEventListener("pointercancel", release);
    stage.addEventListener("pointerleave", onPointerLeave);
    stage.addEventListener("keydown", onKeyDown);
    layout();
    setFront(0);
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      stopType();
      window.removeEventListener("resize", layout);
      stage.removeEventListener("pointerdown", onPointerDown);
      stage.removeEventListener("pointermove", onPointerMove);
      stage.removeEventListener("pointerup", release);
      stage.removeEventListener("pointercancel", release);
      stage.removeEventListener("pointerleave", onPointerLeave);
      stage.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <section aria-roledescription="carousel" aria-label="Sevra features">
      <div
        ref={stageRef}
        className={styles.stage}
        tabIndex={0}
        aria-label="Feature cards. Drag, or use the arrow keys."
      >
        <div className={styles.aura} aria-hidden="true" />
        <div className={styles.floor} aria-hidden="true" />
        <div ref={ringRef} className={styles.ring}>
          <article className={styles.card} aria-roledescription="slide" aria-label="Guest Brief, 1 of 5" aria-hidden="false" style={tint("#e8ab5f")}>
            <div className={cx(styles.face, styles.front)}>
              <div className={styles.top}><span>Guest Brief</span></div>
              <h2 className={styles.long}>Know who&apos;s coming at the push of a button.</h2>
              <div className={styles.panel}>
                <div className={cx(styles.guest, styles.r, styles.d1)}>
                  <span className={styles.avatar}>JR</span>
                  <div><strong>Julien R.</strong><small>Party of 2 · 8:00 PM</small></div>
                  <span className={cx(styles.run, styles.r, styles.d1)}>Brief ready</span>
                </div>
                <div className={cx(styles.fact, styles.r, styles.d2)}>Wine writer for a Montréal food magazine.</div>
                <div className={cx(styles.fact, styles.r, styles.d3)}>Spoke at a Niagara wine festival this summer.</div>
                <div className={cx(styles.fact, styles.r, styles.d4)}>Opened a natural wine bar in 2024.</div>
                <div className={cx(styles.chips, styles.r, styles.d5)}><span className={styles.chip}>Public sources only</span></div>
              </div>
            </div>
            <Back />
          </article>

          <article className={styles.card} aria-roledescription="slide" aria-label="Ask Sevra, 2 of 5" aria-hidden="true" style={tint("#86aef0")}>
            <div className={cx(styles.face, styles.front)}>
              <div className={styles.top}><span>Ask Sevra</span></div>
              <h2>Run the room by asking.</h2>
              <div className={styles.panel}>
                <div className={cx(styles.msg, styles.me, styles.r, styles.d1)}>Block the patio tonight and move the Chen party inside.</div>
                <div className={cx(styles.msg, styles.ai, styles.r, styles.d2)}>
                  <span className={styles.who}>SEVRA</span>
                  <span ref={typedRef} />
                  <span className={styles.caret} aria-hidden="true" />
                </div>
                <div className={styles.ask}>
                  <span>Ask Sevra</span>
                  <i aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#08080a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 10V2M2.5 5.5 6 2l3.5 3.5" /></svg>
                  </i>
                </div>
              </div>
            </div>
            <Back />
          </article>

          <article className={styles.card} aria-roledescription="slide" aria-label="Blacklist, 3 of 5" aria-hidden="true" style={tint("#ee6b58")}>
            <div className={cx(styles.face, styles.front)}>
              <div className={styles.top}><span>Blacklist</span></div>
              <h2>Some guests don&apos;t get a table.</h2>
              <div className={styles.panel}>
                <div className={cx(styles.req, styles.r, styles.d1)}>
                  <div><strong>New request · D. Marsh</strong><small>Party of 2 · Sat 8:30 PM</small></div>
                </div>
                <div className={cx(styles.match, styles.r, styles.d3)}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="7" cy="7" r="5.5" /><path d="M3.2 10.8 10.8 3.2" /></svg>
                  Matches a blocked guest
                </div>
                <span className={styles.stamp}>Not booked</span>
              </div>
            </div>
            <Back />
          </article>

          <article className={styles.card} aria-roledescription="slide" aria-label="Card holds and prepayment, 4 of 5" aria-hidden="true" style={tint("#8cc79c")}>
            <div className={cx(styles.face, styles.front)}>
              <div className={styles.top}><span>Card holds</span></div>
              <h2>Hold a card, or take it up front.</h2>
              <div className={styles.panel}>
                <div className={cx(styles.req, styles.r, styles.d1)}>
                  <div><strong>A. Lambert</strong><small>Party of 4 · Sat 8:00 PM</small></div>
                </div>
                <div className={cx(styles.pay, styles.r, styles.d2)}>
                  <span className={styles.cc} aria-hidden="true" />
                  <span>Visa •••• 4417</span>
                  <b>Held</b>
                </div>
                <div className={cx(styles.fact, styles.r, styles.d3)}>No charge unless they don&apos;t arrive.</div>
                <div className={cx(styles.pay, styles.r, styles.d4)}>
                  <span className={cx(styles.cc, styles.alt)} aria-hidden="true" />
                  <span>Tasting menu</span>
                  <b>Prepaid</b>
                </div>
              </div>
            </div>
            <Back />
          </article>

          <article className={styles.card} aria-roledescription="slide" aria-label="Live SMS, 5 of 5" aria-hidden="true" style={tint("#b89cf0")}>
            <div className={cx(styles.face, styles.front)}>
              <div className={styles.top}><span>Live SMS</span></div>
              <h2>Text your guests, live.</h2>
              <div className={styles.panel}>
                <div className={cx(styles.msg, styles.in, styles.r, styles.d1)}>
                  <span className={styles.who}>GUEST · 7:42 PM</span>Running 15 minutes late. Still okay?
                </div>
                <div className={cx(styles.msg, styles.me, styles.r, styles.d3)}>No problem. We&apos;ll hold your table until 8:15.</div>
                <div className={cx(styles.msg, styles.in, styles.r, styles.d5)}>
                  <span className={styles.who}>GUEST · 7:43 PM</span>Thank you!
                </div>
              </div>
            </div>
            <Back />
          </article>
        </div>
      </div>

      <div className={styles.controls}>
        <p ref={captionRef} className={styles.caption} aria-live="polite">
          {CAPTIONS[0]}
        </p>
      </div>
    </section>
  );
}
